import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import type { EntryCalendarSummaryDay } from "@/shared/cache";
import type { EntryViewDateRange } from "@/shared/contexts/EntryViewStateContext";
import { Button } from "@/v2/components/ui/button";
import {
	type CalendarDayContext,
	CalendarGrid,
	type CalendarWeekContext,
} from "./CalendarGrid";
import { CalendarYearTimeline } from "./CalendarYearTimeline";

export interface EntryCalendarProps {
	selectedDate: Date;
	dateRange: EntryViewDateRange;
	viewedMonth: Date;
	yearSummaryDays: readonly EntryCalendarSummaryDay[];
	filterSlot: ReactNode;
	footerSlot: ReactNode;
	renderDay: (context: CalendarDayContext) => ReactNode;
	renderWeek: (context: CalendarWeekContext) => ReactNode;
	onDayClick: (day: Date) => void;
	onWeekClick: (weekStart: Date, weekEnd: Date) => void;
	onSelectMonth: (month: Date) => void;
	isLoading?: boolean;
	emptyMessage?: string;
}

/**
 * Shared, data-agnostic presentation for Attorney and Admin entry calendars.
 * Callers own query scopes, permissions, filters, navigation and cell content.
 */
export function EntryCalendar({
	selectedDate,
	dateRange,
	viewedMonth,
	yearSummaryDays,
	filterSlot,
	footerSlot,
	renderDay,
	renderWeek,
	onDayClick,
	onWeekClick,
	onSelectMonth,
	isLoading = false,
	emptyMessage,
}: EntryCalendarProps) {
	const goToPreviousYear = () =>
		onSelectMonth(
			new Date(viewedMonth.getFullYear() - 1, viewedMonth.getMonth(), 1),
		);
	const goToNextYear = () =>
		onSelectMonth(
			new Date(viewedMonth.getFullYear() + 1, viewedMonth.getMonth(), 1),
		);

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex items-center gap-3 bg-background px-4 pt-4 pb-2">
				{filterSlot}
				<CalendarYearTimeline
					days={yearSummaryDays}
					viewedMonth={viewedMonth}
					onSelectMonth={onSelectMonth}
					yearControls={
						<div className="flex h-11 shrink-0 items-center rounded-md border border-input bg-background">
							<Button
								variant="ghost"
								size="icon"
								aria-label="Previous year"
								className="h-full w-10 rounded-r-none"
								onClick={goToPreviousYear}
							>
								<ChevronLeft />
							</Button>
							<div className="flex h-full min-w-14 items-center justify-center border-x border-input px-2 text-center text-sm font-semibold tabular-nums text-foreground">
								{viewedMonth.getFullYear()}
							</div>
							<Button
								variant="ghost"
								size="icon"
								aria-label="Next year"
								className="h-full w-10 rounded-l-none"
								onClick={goToNextYear}
							>
								<ChevronRight />
							</Button>
						</div>
					}
				/>
			</div>

			<div className="min-h-0 flex-1 px-4 pt-1 pb-4">
				<div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-background">
					{emptyMessage ? (
						<div className="flex flex-1 items-center justify-center text-muted-foreground">
							{emptyMessage}
						</div>
					) : isLoading ? (
						<div className="flex flex-1 items-center justify-center text-muted-foreground">
							Loading...
						</div>
					) : (
						<>
							<CalendarGrid
								month={viewedMonth}
								selectedDate={selectedDate}
								dateRange={dateRange}
								onDayClick={onDayClick}
								onWeekClick={onWeekClick}
								renderDay={renderDay}
								renderWeek={renderWeek}
							/>
							{footerSlot}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
